import { fetchAuthSession, signOut } from "aws-amplify/auth";
import axios from "axios";
import {
  dateTransformer,
  v2RequestSnakeCaseTransformer,
  v2ResponseCamelCaseTransformer,
} from "mws-utils";

import type { AdminApp } from "@/misc/types";

const getToken = async () => {
  const session = await fetchAuthSession();

  const token = session?.tokens?.accessToken.toString();

  return token ? `Bearer ${token}` : undefined;
};

// External domains that don't support credentials with wildcard CORS
const externalDomains = ["imgix.net"];

const isExternalUrl = (url: string | undefined): boolean => {
  if (!url) return false;

  return externalDomains.some((domain) => url.includes(domain));
};

export default (app: AdminApp) => {
  app.axios = axios;
  app.$http = axios;

  // A simple AxiosTransformer utility to transform date-time strings from
  // JSON data into JS Date objects.
  axios.defaults.transformResponse = [dateTransformer];
  axios.defaults.withCredentials = true;

  app.config.globalProperties.axios = axios;
  app.config.globalProperties.$http = axios;

  // Disable credentials for external domains that use wildcard CORS
  app.axios.interceptors.request.use((config) => {
    if (isExternalUrl(config.url)) {
      config.withCredentials = false;
    }

    return config;
  });

  app.axios.interceptors.request.use(v2RequestSnakeCaseTransformer);
  app.axios.interceptors.request.use(async (config) => {
    try {
      // attaching the auth token before each request
      // it also refreshes the token if needed
      config.headers.Authorization = await getToken();
    } catch {
      signOut();
    }

    return config;
  });

  app.axios.interceptors.response.use(
    v2ResponseCamelCaseTransformer,
    (error) => {
      const err = error;
      if ("response" in err) {
        err.response = v2ResponseCamelCaseTransformer(err.response);
      }

      throw err;
    },
  );
};
